import { unknownImports } from './importRules';
/**
 * DS-0 emission rules — the single source of truth for the AI-emission gate (spec UPT-35 §5).
 *
 * Pure: no `fs`, no `path`, no Node-only globals — so the SAME `lintText` runs both in the
 * `scripts/ds-lint.ts` CLI/CI gate AND in the browser via the TS facade (`dsEmissionGate.ts`),
 * which feeds it into the generate→validate→repair loop. Keeping one implementation here is what
 * stops the runtime gate and the CI gate from drifting apart.
 *
 * A "manifest" is `{ tokenNames:Set, spacePx:Set, radiusPx:Set, components:Set<lowercased name> }`.
 *
 * Previously a `.cjs` file, converted to ESM `.ts` because Vite 6 cannot expose named OR default
 * exports from `module.exports` objects in `.cjs` files during browser ESM consumption.
 */

/** JSX intrinsic/runtime tags that are not DS components and are always allowed. */
export const ALLOWED_TAGS: Set<string> = new Set([
  'Fragment',
  'Suspense',
  'StrictMode',
  'Outlet',
  'Link',
  'NavLink',
  'Form',
  'Meta',
  'Links',
  'Scripts',
  'ScrollRestoration',
]);

/** Runtime shape consumed by lintText (Set-based for O(1) lookups). */
export interface DsManifestSets {
  moduleFiles?: Set<string>;
  tokenNames: Set<string>;
  spacePx: Set<number>;
  radiusPx: Set<number>;
  components: Set<string>;
}

/** Raw `ds-manifest.json` shape (subset consumed by manifestFromJson). */
export interface DsManifestJson {
  moduleFiles?: string[];
  tokenNames: string[];
  scales: { spacePx: number[]; radiusPx: number[] };
  components?: { name: string }[];
}

export type DsViolationRule =
  | 'unknown_import'
  | 'raw_hex'
  | 'raw_color_fn'
  | 'off_scale_px'
  | 'inline_z_index'
  | 'unknown_component';

export interface DsViolation {
  rule: DsViolationRule;
  message: string;
  match: string;
  line: number;
  column: number;
}

function lineColOf(text: string, index: number): { line: number; column: number } {
  const before = text.slice(0, index);
  const lines = before.split(/\r?\n/);
  return { line: lines.length, column: lines[lines.length - 1].length + 1 };
}

/**
 * Lint a single artifact string. Returns an array of violations:
 * { rule, message, match, line, column }
 */
export function lintText(text: string, manifest: DsManifestSets): DsViolation[] {
  const m = manifest;
  const violations: DsViolation[] = [];
  const push = (rule: DsViolationRule, match: string, index: number, message: string) => {
    const { line, column } = lineColOf(text, index);
    violations.push({ rule, message, match, line, column });
  };

  for(const issue of unknownImports(text,[...(m.moduleFiles??[])]))push('unknown_import',issue.match,issue.index,'Import outside the component library allowlist.');

  // 1) raw hex
  for (const x of text.matchAll(/#[0-9a-fA-F]{3,8}\b/g)) {
    push('raw_hex', x[0], x.index ?? 0, `Raw hex color "${x[0]}" — reference a token, e.g. var(--c-...).`);
  }

  // 2) raw color functions
  for (const x of text.matchAll(/\b(rgb|rgba|hsl|hsla)\s*\(/g)) {
    push('raw_color_fn', x[0], x.index ?? 0, `Raw color function "${x[1]}()" — reference a token instead.`);
  }

  // 3) raw px off the spacing/radius scale (1px/2px hairlines always allowed)
  for (const x of text.matchAll(/(?<![\w.-])(\d+(?:\.\d+)?)px\b/g)) {
    const v = Number(x[1]);
    const onScale = Number.isInteger(v) && (m.spacePx.has(v) || m.radiusPx.has(v) || v === 1 || v === 2);
    if (!onScale) {
      push('off_scale_px', x[0], x.index ?? 0, `"${x[0]}" is off the spacing/radius scale — use var(--space-*) / var(--radius-*).`);
    }
  }

  // 4) inline numeric z-index
  for (const x of text.matchAll(/\b(?:z-index|zIndex)\s*:\s*('|"|`)?\s*(-?\d+)/g)) {
    push('inline_z_index', x[0], x.index ?? 0, `Inline z-index "${x[2]}" — use var(--z-*) from the z-scale.`);
  }

  // 5a) unknown component names — PascalCase JSX tags not in the catalog/allowlist
  for (const x of text.matchAll(/<([A-Z][A-Za-z0-9]*)\b/g)) {
    // Type arguments (Set<T>, ComponentType<Props>) are not JSX elements.
    const before = text.slice(0, x.index ?? 0);
    const after = text.slice((x.index ?? 0) + x[0].length);
    if (after.startsWith('<') || (/[\w$]$/.test(before) && !/\b(?:return|yield)$/.test(before) && /^[>,]/.test(after))) continue;
    const name = x[1];
    if (!m.components.has(name.toLowerCase()) && !ALLOWED_TAGS.has(name)) {
      push('unknown_component', x[0], x.index ?? 0, `Unknown component <${name}> — not in the DS-0 catalog.`);
    }
  }
  // 5b) unknown component names in JSON layout descriptors ("component": "x")
  for (const x of text.matchAll(/"component"\s*:\s*"([^"]+)"/g)) {
    if (!m.components.has(x[1].toLowerCase())) {
      push('unknown_component', x[0], x.index ?? 0, `Unknown component "${x[1]}" — not in the DS-0 catalog.`);
    }
  }

  return violations;
}

/** Build the manifest Sets from the raw ds-manifest.json object (shared by CLI + browser). */
export function manifestFromJson(m: DsManifestJson): DsManifestSets {
  return {
    moduleFiles: new Set(m.moduleFiles??[]),
    tokenNames: new Set(m.tokenNames),
    spacePx: new Set(m.scales.spacePx),
    radiusPx: new Set(m.scales.radiusPx),
    components: new Set((m.components ?? []).map((c) => c.name.toLowerCase())),
  };
}
